import Product from "./product";
import Form, { SEATED_FORM_ID } from "./rules-block/form";
import Rate from "./rules-block/rate";
import RateCategory from "./rules-block/rateCategory";
import FormField from "./rules-block/formField";
import Date from "./rules-block/date";
import EventConfig from "./event-config";
import SubProduct from "./subProduct";

export default class Rule {
	constructor({
		id,
		event,
		date,
		product,
		subProduct,
		form,
		formField,
		question,
		answer,
		rate,
		rateCategory,
		modified,
		removed,
		version
	} = {}) {
		if (rate && rateCategory) {
			throw new Error(
				"Rule cannot have both rate and rateCategory defined. Choose one."
			);
		}

		this.id = id;
		this.date = date;
		this.event = event;
		this.product = product;
		this.subProduct = subProduct;
		this.form = form;
		this.formField = formField;
		this.question = question;
		this.answer = answer;
		this.rate = rate;
		this.rateCategory = rateCategory;
		this.modified = modified;
		this.removed = removed;
		this.version = version;
	}

	static buildFromJSON(json) {
		const isSeatedFormQuestion = ["ivar_access", "ivar_zone"].includes(
			json.question
		);

		let seatedForm = null;
		let seatedFormField = null;

		if (isSeatedFormQuestion) {
			seatedForm = Form.buildFromJSON({
				id_form: SEATED_FORM_ID,
				name: "",
				event: null,
				rates: [],
				form_fields: [
					{ id: "ivar_zone", label: "", choices: null },
					{ id: "ivar_access", label: "", choices: null }
				]
			});

			seatedFormField = FormField.buildFromJSON({
				id: json.question,
				label: "",
				choices: null
			});
		}

		return new Rule({
			id: json.id,
			event: json.event ? EventConfig.buildFromJSON(json.event) : null,
			date: json.date ? Date.buildFromJSON(json.date) : null,
			product: json.product ? Product.buildFromJSON(json.product) : null,
			subProduct: json.sub_product
				? SubProduct.buildFromJSON(json.sub_product)
				: null,
			form: json.form ? Form.buildFromJSON(json.form) : seatedForm,
			question: json.question ?? "",
			answer: json.answer ?? "",
			rate: json.rate ? Rate.buildFromJSON(json.rate) : null,
			rateCategory: json.rate_category
				? RateCategory.buildFromJSON(json.rate_category)
				: null,
			formField: json.form_field
				? FormField.buildFromJSON(json.form_field)
				: seatedFormField || "",
			removed: json.removed
		});
	}

	toJSON() {
		return {
			id: this.id,
			event: this.event
				? { id: this.event.id, name: this.event.name }
				: null,
			date: this.date ? { id: this.date.id, name: this.date.date } : null,
			product: this.product
				? { id: this.product.id, name: this.product.name }
				: null,
			sub_product: this.subProduct
				? { id: this.subProduct.id, name: this.subProduct.name }
				: null,
			form: this.form ? { id: this.form.id, name: this.form.name } : null,
			form_field: this.formField
				? { id: this.formField.id, label: this.formField.label }
				: null,
			question: this.question ?? null,
			answer: this.answer ?? null,
			rate: this.rate ? { id: this.rate.id, name: this.rate.name } : null,
			rate_category: this.rateCategory
				? { id: this.rateCategory.id, name: this.rateCategory.name }
				: null,
			count: this.count || 0,
			strict_count: this.strict_count || 0,
			version: this.version || 0,
			removed: this.removed
		};
	}

	rulesToApi() {
		return {
			event_id: this.event?.id || null,
			answer: this.answer ?? null,
			question: this.question ?? null,
			removed: this.removed ?? false,
			rate_category_id: this.rateCategory?.id ?? null,
			rate_id: this.rate?.id ?? null,
			form_id: this.form?.id_form ?? this.form?.id ?? null,
			product_id: this.product?.id ?? null,
			sub_product_id: this.subProduct?.id ?? null,
			form_field_id: this.formField?.id ?? this.formField ?? null,
			date_id: this.date?.id ?? this.date ?? null
		};
	}

	isFromApi() {
		return !!this.id;
	}

	getId() {
		return this.id;
	}

	getEvent() {
		return this.event;
	}

	getProduct() {
		return this.product;
	}

	getSubProduct() {
		return this.subProduct;
	}

	getForm() {
		return this.form;
	}

	getQuestion() {
		return this.question;
	}

	getAnswer() {
		if (this.answer !== "") {
			return this.answer;
		} else return "";
	}

	getRate() {
		return this.rate;
	}

	getRateCategory() {
		return this.rateCategory;
	}

	getFormField() {
		return this.formField;
	}

	getDate() {
		return this.date;
	}

	getVersion() {
		return this.version;
	}

	hasNoConditions() {
		return (
			!this.getProduct() &&
			!this.getSubProduct() &&
			!this.getForm() &&
			!this.getFormField() &&
			!this.getAnswer() &&
			!this.getQuestion() &&
			!this.getDate()
		);
	}

	hasOnlyEvent() {
		return (
			!this.getRate() && !this.getRateCategory() && this.hasNoConditions()
		);
	}

	// used only to group fully saved rules (have version)
	hasRuleCreated() {
		return this.isFromApi() && this.getEvent() && this.hasNoConditions();
	}

	getGroupedKey() {
		// If the rule has event + (rate or category) (but no form)
		if (
			this.getEvent() &&
			(this.getRateCategory() || this.getRate()) &&
			!this.getForm()
		) {
			return [
				`event_${this.getEvent()?.id}`,
				`form_${this.getForm()?.id}`,
				`rates_and_categories_with_form`
			].join("_");
		}

		// If the rule has event + (rate or category) + product/subProduct (but no form)
		if (
			this.getEvent() &&
			(this.getRateCategory() || this.getRate()) &&
			this.getProduct() &&
			!this.getForm()
		) {
			return [
				`event_${this.getEvent()?.id}`,
				`product_${this.getProduct()?.id}`,
				`subproduct_${this.getSubProduct()?.id}`,
				`rates_and_categories`
			].join("_");
		}

		// If the rule has event + (rate or category) without form or product
		if (
			this.getEvent() &&
			(this.getRateCategory() || this.getRate()) &&
			!this.getForm() &&
			!this.getProduct()
		) {
			return `event_${this.getEvent()?.id}_rates_and_categories`;
		}

		if (this.hasOnlyEvent()) {
			return `event_${this.getEvent()?.id}_only`;
		}

		// Fallback : concatenate all relevant fields
		return [
			`event_${this.getEvent()?.id}`,
			`category_${this.getRateCategory()?.id}`,
			`rate_${this.getRate()?.id}`,
			`form_${this.getForm()?.id}`,
			`product_${this.getProduct()?.id}`,
			`subproduct_${this.getSubProduct()?.id}`,
			`question_${this.getQuestion()}`,
			`answer_${this.getAnswer()}`,
			`date_${this.getDate()?.id}`
		].join("_");
	}
}
